import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../ui/Button';
import type { PoImportLine } from '../../../types/nurseryRetail';

/** Something a purchase order line can be: an umbrella product or a stock item. */
export interface MatchOption {
  kind: 'product' | 'stock_item';
  id: number;
  label: string;
}

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm';
const valueOf = (o: MatchOption) => `${o.kind}:${o.id}`;

/**
 * Says which of ours an unmatched purchase order line is. The choice is
 * remembered against the line's barcode and the retailer's item number, so
 * the same tree is matched by itself on every later purchase order.
 */
export function PoLineMatcher({ line, options, busy, onMatch }: {
  line: PoImportLine;
  options: MatchOption[];
  busy: boolean;
  onMatch: (option: MatchOption) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'PoLineMatcher' });
  const [search, setSearch] = useState('');
  const [value, setValue] = useState('');
  const id = `match-${line.line_number ?? line.item_number ?? line.barcode}`;

  const shown = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const hits = words.length
      ? options.filter((o) => words.every((w) => o.label.toLowerCase().includes(w)))
      : options;
    return hits.slice(0, 200);
  }, [options, search]);

  const chosen = options.find((o) => valueOf(o) === value) || null;

  return (
    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
      <p className="text-sm text-amber-900">
        {t('intro')}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label htmlFor={`${id}-search`} className="block text-xs font-medium text-neutral-700 mb-1">{t('search')}</label>
          <input id={`${id}-search`} className={inputCls} value={search} placeholder={t('searchPlaceholder')}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div>
          <label htmlFor={`${id}-choice`} className="block text-xs font-medium text-neutral-700 mb-1">
            {t('choiceLabel', { shown: `${shown.length}${shown.length < options.length && !search ? '+' : ''}` })}
          </label>
          <select id={`${id}-choice`} className={inputCls} value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">{t('choose')}</option>
            {shown.map((o) => (
              <option key={valueOf(o)} value={valueOf(o)}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      <Button size="sm" disabled={busy || !chosen} onClick={() => chosen && onMatch(chosen)}>
        {busy ? t('saving') : t('match')}
      </Button>
    </div>
  );
}
